/**
 * AWS Cognito AuthClient Implementation
 *
 * Direct AWS SDK implementation for authentication using AWS Cognito.
 * This replaces Amplify Auth with direct SDK calls for better control and flexibility.
 */

import {
  CognitoIdentityProviderClient,
  InitiateAuthCommand,
  SignUpCommand,
  ConfirmSignUpCommand,
  ForgotPasswordCommand,
  ConfirmForgotPasswordCommand,
  ChangePasswordCommand,
  UpdateUserAttributesCommand,
  AttributeType,
} from '@aws-sdk/client-cognito-identity-provider';
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';
import {
  AuthClientInterface,
  AuthClientConfig,
  SignInParams,
  SignUpParams,
  ConfirmSignUpParams,
  ForgotPasswordParams,
  ConfirmForgotPasswordParams,
  ChangePasswordParams,
  UpdateUserAttributesParams,
  CognitoTokens,
  CognitoUserData,
  TokenCache,
  AuthError,
} from '../types/AuthClient';
import { migrationLogger, MigrationPhase, MigrationStatus } from '../../utils/migrationLogger';

const TOKEN_STORAGE_KEY = 'cognito_tokens';
const USER_STORAGE_KEY = 'cognito_user';

// Keys used by AwsAuthContext for compatibility
const AUTH_TOKEN_KEYS = {
  ACCESS_TOKEN: 'accessToken',
  ID_TOKEN: 'idToken',
  REFRESH_TOKEN: 'refreshToken',
};

class CognitoAuthClient implements AuthClientInterface {
  private config: AuthClientConfig;
  private tokenCache: TokenCache;
  private client: CognitoIdentityProviderClient;

  constructor(config: AuthClientConfig) {
    this.config = config;
    this.tokenCache = {
      tokens: null,
      user: null,
    };
    this.client = new CognitoIdentityProviderClient({
      region: config.region,
    });
    this.loadCachedTokens();
  }

  private loadCachedTokens(): void {
    if (typeof window === 'undefined') return;

    try {
      // First try to load from AwsAuthContext's storage format
      const accessToken = localStorage.getItem(AUTH_TOKEN_KEYS.ACCESS_TOKEN);
      const idToken = localStorage.getItem(AUTH_TOKEN_KEYS.ID_TOKEN);
      const refreshToken = localStorage.getItem(AUTH_TOKEN_KEYS.REFRESH_TOKEN);

      if (accessToken && idToken && refreshToken) {
        // Parse tokens to get expiration time
        try {
          const accessTokenPayload = this.parseJwtToken(accessToken);
          const exp = accessTokenPayload.exp;

          if (exp && exp * 1000 > Date.now()) {
            const tokens: CognitoTokens = {
              accessToken,
              idToken,
              refreshToken,
              expiresAt: exp * 1000,
            };
            this.tokenCache.tokens = tokens;

            // Extract user data from tokens
            const user = this.extractUserFromTokens(idToken, accessToken);
            this.tokenCache.user = user;

            // Also save in our format for consistency
            this.saveCachedTokens(tokens, user);
            return;
          }
        } catch (e) {
          console.warn('Failed to parse tokens from AwsAuthContext storage:', e);
        }
      }

      // Fallback to our own storage format
      const tokensStr = localStorage.getItem(TOKEN_STORAGE_KEY);
      const userStr = localStorage.getItem(USER_STORAGE_KEY);

      if (tokensStr) {
        const tokens = JSON.parse(tokensStr) as CognitoTokens;
        if (tokens.expiresAt > Date.now()) {
          this.tokenCache.tokens = tokens;
        } else {
          this.clearCache();
        }
      }

      if (userStr) {
        this.tokenCache.user = JSON.parse(userStr) as CognitoUserData;
      }
    } catch (error) {
      console.error('Failed to load cached tokens:', error);
      this.clearCache();
    }
  }

  private saveCachedTokens(tokens: CognitoTokens, user: CognitoUserData): void {
    if (typeof window === 'undefined') return;

    try {
      // Save in both formats for compatibility
      // Save in AwsAuthContext format
      localStorage.setItem(AUTH_TOKEN_KEYS.ACCESS_TOKEN, tokens.accessToken);
      localStorage.setItem(AUTH_TOKEN_KEYS.ID_TOKEN, tokens.idToken);
      localStorage.setItem(AUTH_TOKEN_KEYS.REFRESH_TOKEN, tokens.refreshToken);

      // Save in our own format
      localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(tokens));
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));

      this.tokenCache.tokens = tokens;
      this.tokenCache.user = user;
    } catch (error) {
      console.error('Failed to save tokens to cache:', error);
    }
  }

  private clearCache(): void {
    if (typeof window === 'undefined') return;

    // Clear both storage formats
    localStorage.removeItem(AUTH_TOKEN_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(AUTH_TOKEN_KEYS.ID_TOKEN);
    localStorage.removeItem(AUTH_TOKEN_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(USER_STORAGE_KEY);

    this.tokenCache.tokens = null;
    this.tokenCache.user = null;
  }

  private parseJwtToken(token: string): Record<string, any> {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      throw new AuthError('Failed to parse JWT token', 'InvalidParameterException');
    }
  }

  private extractUserFromTokens(idToken: string, accessToken: string): CognitoUserData {
    const idTokenPayload = this.parseJwtToken(idToken);
    const accessTokenPayload = this.parseJwtToken(accessToken);

    return {
      username: idTokenPayload['cognito:username'] || idTokenPayload.sub,
      email: idTokenPayload.email || '',
      userId: idTokenPayload.sub,
      attributes: {
        email: idTokenPayload.email || '',
        given_name: idTokenPayload.given_name || '',
        family_name: idTokenPayload.family_name || '',
        'custom:organization': idTokenPayload['custom:organization'] || '',
        ...idTokenPayload,
      },
      groups: idTokenPayload['cognito:groups'] || accessTokenPayload['cognito:groups'] || [],
    };
  }

  async signIn(params: SignInParams): Promise<CognitoTokens> {
    migrationLogger.log(
      MigrationPhase.AUTH,
      MigrationStatus.IN_PROGRESS,
      'Attempting sign in with AWS SDK',
      { username: params.username }
    );

    return new Promise((resolve, reject) => {
      try {
        const poolData = {
          UserPoolId: this.config.userPoolId,
          ClientId: this.config.clientId,
        };
        const userPool = new CognitoUserPool(poolData);

        const userData = {
          Username: params.username,
          Pool: userPool,
        };
        const cognitoUser = new CognitoUser(userData);

        const authenticationData = {
          Username: params.username,
          Password: params.password,
        };
        const authenticationDetails = new AuthenticationDetails(authenticationData);

        cognitoUser.authenticateUser(authenticationDetails, {
          onSuccess: (session: CognitoUserSession) => {
            try {
              const idToken = session.getIdToken().getJwtToken();
              const accessToken = session.getAccessToken().getJwtToken();
              const refreshToken = session.getRefreshToken().getToken();
              const expiresAt = session.getAccessToken().getExpiration() * 1000;

              const tokens: CognitoTokens = {
                accessToken,
                idToken,
                refreshToken,
                expiresAt,
              };

              const user = this.extractUserFromTokens(tokens.idToken, tokens.accessToken);
              this.saveCachedTokens(tokens, user);

              migrationLogger.log(
                MigrationPhase.AUTH,
                MigrationStatus.COMPLETED,
                'Sign in completed successfully',
                { username: params.username }
              );

              resolve(tokens);
            } catch (error) {
              migrationLogger.logError(MigrationPhase.AUTH, 'Sign in failed', error as Error, {
                username: params.username,
              });
              reject(this.handleAuthError(error));
            }
          },
          onFailure: (error: any) => {
            migrationLogger.logError(MigrationPhase.AUTH, 'Sign in failed', error as Error, {
              username: params.username,
            });

            if (error.name === 'UserNotFoundException') {
              reject(new AuthError('User not found', 'UserNotFoundException'));
            } else if (error.name === 'NotAuthorizedException') {
              reject(new AuthError('Incorrect username or password', 'NotAuthorizedException'));
            } else if (error.name === 'UserNotConfirmedException') {
              reject(new AuthError('User not confirmed', 'UserNotConfirmedException'));
            } else {
              reject(this.handleAuthError(error));
            }
          },
        });
      } catch (error: any) {
        migrationLogger.logError(MigrationPhase.AUTH, 'Sign in failed', error as Error, {
          username: params.username,
        });
        reject(this.handleAuthError(error));
      }
    });
  }

  async signUp(params: SignUpParams): Promise<{ userSub: string }> {
    migrationLogger.log(
      MigrationPhase.AUTH,
      MigrationStatus.IN_PROGRESS,
      'Attempting sign up with AWS SDK',
      { username: params.username, email: params.email }
    );

    try {
      const userAttributes: AttributeType[] = [
        { Name: 'email', Value: params.email },
        { Name: 'given_name', Value: params.firstName },
        { Name: 'family_name', Value: params.lastName },
        { Name: 'custom:organization', Value: params.organization },
      ];

      const signUpResponse = await this.client.send(
        new SignUpCommand({
          ClientId: this.config.clientId,
          Username: params.username,
          Password: params.password,
          UserAttributes: userAttributes,
        })
      );

      if (!signUpResponse.UserSub) {
        throw new AuthError('Sign up failed - no user sub returned', 'UnknownError');
      }

      migrationLogger.log(
        MigrationPhase.AUTH,
        MigrationStatus.COMPLETED,
        'Sign up completed successfully',
        { username: params.username, userSub: signUpResponse.UserSub }
      );

      return { userSub: signUpResponse.UserSub };
    } catch (error: any) {
      migrationLogger.logError(MigrationPhase.AUTH, 'Sign up failed', error as Error, {
        username: params.username,
      });

      if (error.name === 'UsernameExistsException') {
        throw new AuthError('Username already exists', 'UsernameExistsException');
      }
      if (error.name === 'InvalidPasswordException') {
        throw new AuthError('Invalid password', 'InvalidPasswordException');
      }
      if (error.name === 'InvalidParameterException') {
        throw new AuthError('Invalid parameter', 'InvalidParameterException');
      }

      throw this.handleAuthError(error);
    }
  }

  async confirmSignUp(params: ConfirmSignUpParams): Promise<void> {
    migrationLogger.log(
      MigrationPhase.AUTH,
      MigrationStatus.IN_PROGRESS,
      'Confirming sign up with AWS SDK',
      { username: params.username }
    );

    try {
      await this.client.send(
        new ConfirmSignUpCommand({
          ClientId: this.config.clientId,
          Username: params.username,
          ConfirmationCode: params.code,
        })
      );

      migrationLogger.log(
        MigrationPhase.AUTH,
        MigrationStatus.COMPLETED,
        'Confirm sign up completed successfully',
        { username: params.username }
      );
    } catch (error: any) {
      migrationLogger.logError(MigrationPhase.AUTH, 'Confirm sign up failed', error as Error, {
        username: params.username,
      });

      if (error.name === 'CodeMismatchException') {
        throw new AuthError('Invalid verification code', 'CodeMismatchException');
      }
      if (error.name === 'ExpiredCodeException') {
        throw new AuthError('Verification code expired', 'ExpiredCodeException');
      }

      throw this.handleAuthError(error);
    }
  }

  async forgotPassword(params: ForgotPasswordParams): Promise<void> {
    migrationLogger.log(
      MigrationPhase.AUTH,
      MigrationStatus.IN_PROGRESS,
      'Initiating forgot password with AWS SDK',
      { username: params.username }
    );

    try {
      await this.client.send(
        new ForgotPasswordCommand({
          ClientId: this.config.clientId,
          Username: params.username,
        })
      );

      migrationLogger.log(
        MigrationPhase.AUTH,
        MigrationStatus.COMPLETED,
        'Forgot password initiated successfully',
        { username: params.username }
      );
    } catch (error: any) {
      migrationLogger.logError(MigrationPhase.AUTH, 'Forgot password failed', error as Error, {
        username: params.username,
      });

      if (error.name === 'UserNotFoundException') {
        throw new AuthError('User not found', 'UserNotFoundException');
      }
      if (error.name === 'LimitExceededException') {
        throw new AuthError('Too many requests', 'TooManyRequestsException');
      }

      throw this.handleAuthError(error);
    }
  }

  async confirmForgotPassword(params: ConfirmForgotPasswordParams): Promise<void> {
    migrationLogger.log(
      MigrationPhase.AUTH,
      MigrationStatus.IN_PROGRESS,
      'Confirming forgot password with AWS SDK',
      { username: params.username }
    );

    try {
      await this.client.send(
        new ConfirmForgotPasswordCommand({
          ClientId: this.config.clientId,
          Username: params.username,
          ConfirmationCode: params.code,
          Password: params.newPassword,
        })
      );

      migrationLogger.log(
        MigrationPhase.AUTH,
        MigrationStatus.COMPLETED,
        'Confirm forgot password completed successfully',
        { username: params.username }
      );
    } catch (error: any) {
      migrationLogger.logError(
        MigrationPhase.AUTH,
        'Confirm forgot password failed',
        error as Error,
        { username: params.username }
      );

      if (error.name === 'CodeMismatchException') {
        throw new AuthError('Invalid verification code', 'CodeMismatchException');
      }
      if (error.name === 'ExpiredCodeException') {
        throw new AuthError('Verification code expired', 'ExpiredCodeException');
      }
      if (error.name === 'InvalidPasswordException') {
        throw new AuthError('Invalid password', 'InvalidPasswordException');
      }

      throw this.handleAuthError(error);
    }
  }

  async changePassword(params: ChangePasswordParams): Promise<void> {
    migrationLogger.log(
      MigrationPhase.AUTH,
      MigrationStatus.IN_PROGRESS,
      'Changing password with AWS SDK'
    );

    try {
      const accessToken = await this.getValidAccessToken();

      await this.client.send(
        new ChangePasswordCommand({
          AccessToken: accessToken,
          PreviousPassword: params.oldPassword,
          ProposedPassword: params.newPassword,
        })
      );

      migrationLogger.log(
        MigrationPhase.AUTH,
        MigrationStatus.COMPLETED,
        'Change password completed successfully'
      );
    } catch (error: any) {
      migrationLogger.logError(MigrationPhase.AUTH, 'Change password failed', error as Error);

      if (error.name === 'NotAuthorizedException') {
        throw new AuthError('Incorrect old password', 'NotAuthorizedException');
      }
      if (error.name === 'InvalidPasswordException') {
        throw new AuthError('Invalid new password', 'InvalidPasswordException');
      }
      if (error.name === 'LimitExceededException') {
        throw new AuthError('Too many requests', 'TooManyRequestsException');
      }

      throw this.handleAuthError(error);
    }
  }

  async signOut(): Promise<void> {
    migrationLogger.log(
      MigrationPhase.AUTH,
      MigrationStatus.IN_PROGRESS,
      'Signing out with AWS SDK'
    );

    this.clearCache();

    migrationLogger.log(
      MigrationPhase.AUTH,
      MigrationStatus.COMPLETED,
      'Sign out completed successfully'
    );
  }

  async getCurrentUser(): Promise<CognitoUserData | null> {
    if (this.tokenCache.user && this.tokenCache.tokens) {
      if (this.tokenCache.tokens.expiresAt > Date.now()) {
        return this.tokenCache.user;
      }

      try {
        await this.refreshSession();
        return this.tokenCache.user;
      } catch (error) {
        this.clearCache();
        return null;
      }
    }

    return null;
  }

  async getValidAccessToken(): Promise<string> {
    if (!this.tokenCache.tokens) {
      throw new AuthError('No tokens available', 'NotAuthorizedException');
    }

    if (this.tokenCache.tokens.expiresAt <= Date.now() + 60000) {
      await this.refreshSession();
    }

    if (!this.tokenCache.tokens) {
      throw new AuthError('Failed to refresh token', 'NotAuthorizedException');
    }

    return this.tokenCache.tokens.accessToken;
  }

  async getValidIdToken(): Promise<string> {
    if (!this.tokenCache.tokens) {
      throw new AuthError('No tokens available', 'NotAuthorizedException');
    }

    if (this.tokenCache.tokens.expiresAt <= Date.now() + 60000) {
      await this.refreshSession();
    }

    if (!this.tokenCache.tokens || !this.tokenCache.tokens.idToken) {
      throw new AuthError(
        'Failed to refresh token or ID token not available',
        'NotAuthorizedException'
      );
    }

    return this.tokenCache.tokens.idToken;
  }

  async refreshSession(): Promise<CognitoTokens> {
    migrationLogger.log(
      MigrationPhase.AUTH,
      MigrationStatus.IN_PROGRESS,
      'Refreshing session with AWS SDK'
    );

    try {
      if (!this.tokenCache.tokens?.refreshToken) {
        throw new AuthError('No refresh token available', 'NotAuthorizedException');
      }

      const refreshResponse = await this.client.send(
        new InitiateAuthCommand({
          ClientId: this.config.clientId,
          AuthFlow: 'REFRESH_TOKEN_AUTH',
          AuthParameters: {
            REFRESH_TOKEN: this.tokenCache.tokens.refreshToken,
          },
        })
      );

      if (!refreshResponse.AuthenticationResult) {
        throw new AuthError('Failed to refresh session', 'NotAuthorizedException');
      }

      const authResult = refreshResponse.AuthenticationResult;
      if (!authResult.IdToken || !authResult.AccessToken) {
        throw new AuthError('Missing tokens in refresh response', 'UnknownError');
      }

      const expiresIn = authResult.ExpiresIn || 3600;
      const tokens: CognitoTokens = {
        accessToken: authResult.AccessToken,
        idToken: authResult.IdToken,
        refreshToken: this.tokenCache.tokens.refreshToken,
        expiresAt: Date.now() + expiresIn * 1000,
      };

      const user = this.extractUserFromTokens(tokens.idToken, tokens.accessToken);
      this.saveCachedTokens(tokens, user);

      migrationLogger.log(
        MigrationPhase.AUTH,
        MigrationStatus.COMPLETED,
        'Session refresh completed successfully'
      );

      return tokens;
    } catch (error) {
      migrationLogger.logError(MigrationPhase.AUTH, 'Session refresh failed', error as Error);
      this.clearCache();
      throw this.handleAuthError(error);
    }
  }

  async updateUserAttributes(params: UpdateUserAttributesParams): Promise<void> {
    migrationLogger.log(
      MigrationPhase.AUTH,
      MigrationStatus.IN_PROGRESS,
      'Updating user attributes with AWS SDK'
    );

    try {
      const accessToken = await this.getValidAccessToken();

      const userAttributes: AttributeType[] = Object.entries(params.attributes).map(
        ([key, value]) => ({
          Name: key,
          Value: value,
        })
      );

      await this.client.send(
        new UpdateUserAttributesCommand({
          AccessToken: accessToken,
          UserAttributes: userAttributes,
        })
      );

      if (this.tokenCache.user) {
        this.tokenCache.user.attributes = {
          ...this.tokenCache.user.attributes,
          ...params.attributes,
        };
        if (typeof window !== 'undefined') {
          localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(this.tokenCache.user));
        }
      }

      migrationLogger.log(
        MigrationPhase.AUTH,
        MigrationStatus.COMPLETED,
        'Update user attributes completed successfully'
      );
    } catch (error: any) {
      migrationLogger.logError(
        MigrationPhase.AUTH,
        'Update user attributes failed',
        error as Error
      );

      if (error.name === 'InvalidParameterException') {
        throw new AuthError('Invalid attribute', 'InvalidParameterException');
      }

      throw this.handleAuthError(error);
    }
  }

  async listGroups(): Promise<string[]> {
    const user = await this.getCurrentUser();
    if (!user) {
      return [];
    }

    if (user.groups && user.groups.length > 0) {
      return user.groups;
    }

    if (this.tokenCache.tokens?.idToken) {
      try {
        const idTokenPayload = this.parseJwtToken(this.tokenCache.tokens.idToken);
        return idTokenPayload['cognito:groups'] || [];
      } catch (error) {
        console.error('Failed to parse groups from token:', error);
        return [];
      }
    }

    return [];
  }

  /**
   * Decode and validate a JWT token for server-side usage
   * This method does not use the token cache and is suitable for API routes
   */
  async decodeServerToken(token: string): Promise<CognitoUserData | null> {
    try {
      // For server-side, we should ideally verify the token signature
      // For now, we'll decode the token and extract user information
      // In production, you should verify the token against Cognito's public keys

      // Try to parse as ID token first (contains more user info)
      const tokenPayload = this.parseJwtToken(token);

      // Check if token is expired
      const exp = tokenPayload.exp;
      if (exp && exp * 1000 < Date.now()) {
        return null;
      }

      // Extract user data from the token
      const userData: CognitoUserData = {
        username: tokenPayload['cognito:username'] || tokenPayload.sub,
        email: tokenPayload.email || '',
        userId: tokenPayload.sub,
        attributes: {
          email: tokenPayload.email || '',
          given_name: tokenPayload.given_name || '',
          family_name: tokenPayload.family_name || '',
          'custom:organization': tokenPayload['custom:organization'] || '',
          ...tokenPayload,
        },
        groups: tokenPayload['cognito:groups'] || [],
      };

      return userData;
    } catch (error) {
      console.error('Failed to decode server token:', error);
      return null;
    }
  }

  /**
   * Get groups from a JWT token for server-side usage
   */
  async getGroupsFromToken(token: string): Promise<string[]> {
    try {
      const tokenPayload = this.parseJwtToken(token);
      return tokenPayload['cognito:groups'] || [];
    } catch (error) {
      console.error('Failed to get groups from token:', error);
      return [];
    }
  }

  private handleAuthError(error: unknown): AuthError {
    if (error instanceof AuthError) {
      return error;
    }

    if (error instanceof Error) {
      return new AuthError(error.message, 'UnknownError');
    }

    return new AuthError('Unknown authentication error', 'UnknownError');
  }
}

let authClientInstance: CognitoAuthClient | null = null;

export function getAuthClient(config?: AuthClientConfig): CognitoAuthClient {
  if (!authClientInstance && config) {
    authClientInstance = new CognitoAuthClient(config);
  }

  if (!authClientInstance) {
    throw new Error('AuthClient not initialized. Call getAuthClient with config first.');
  }

  return authClientInstance;
}

export function initializeAuthClient(config: AuthClientConfig): CognitoAuthClient {
  authClientInstance = new CognitoAuthClient(config);
  return authClientInstance;
}

export { CognitoAuthClient };
export type { AuthClientInterface };
